import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useQueryParams } from '@upright/ui/common/generic/hooks/url-handling/query-params';
import {
  desktop1201pxOrWider,
  MobileOnly,
  mobileOrNarrower,
  TabletOrWiderOnly,
} from '@upright/ui/common/generic/layout/breakpoint-utils';
import styled, { css } from 'styled-components';
import {
  allFeatures,
  subscriptionData,
  SubscriptionTierInTable,
} from '../../../../../common/activities/contact-forms/company-subscription/subscription-tiers';
import { subscriptionModalParamKeys, SubscriptionModalQueryParams } from '../subscription-modal/types';
import { SubscriptionTableHead } from './subscription-table-head';
import { SubscriptionTableMobile } from './subscription-table-mobile';
import { SubscriptionTableRow } from './subscription-table-row';
import { SubscriptionTableLayout } from './types';

interface Props {
  layout?: SubscriptionTableLayout;
}

export const SubscriptionTable = (props: Props) => {
  const [, setQueryParams] = useQueryParams<SubscriptionModalQueryParams>(subscriptionModalParamKeys);

  const layout: SubscriptionTableLayout = props.layout || 'normal';

  function onTierClick(tier: SubscriptionTierInTable) {
    setQueryParams({ subscribe: 'true', tier }, 'push');
  }

  return (
    <TableContainer layout={layout}>
      <MobileOnly>
        <SubscriptionTableMobile {...{ onTierClick }} />
      </MobileOnly>
      <TabletOrWiderOnly>
        <Table>
          <SubscriptionTableHead {...{ onTierClick, layout }} />
          <tbody>
            {allFeatures.map(feature => {
              return (
                <SubscriptionTableRow
                  layout={layout}
                  key={feature}
                  featureText={feature}
                  isBasic={feature in subscriptionData.Free}
                  isStandard={feature in subscriptionData.Standard}
                  isAdvanced={feature in subscriptionData.Advanced}
                />
              );
            })}
          </tbody>
        </Table>
      </TabletOrWiderOnly>
    </TableContainer>
  );
};

// Todo: perhaps define the CSS as styled-components where they are used – easier readability. -RN

const TableContainer = styled.div<{ layout: SubscriptionTableLayout }>`
  ${desktop1201pxOrWider(css`
    max-width: 1200px;
  `)}
  span.subscription-table-text-cell-text {
    ${mobileOrNarrower(
      css`
        ${getTextStyle({ size: 'xxs' })}
      `,
    )}
    ${props => {
      switch (props.layout) {
        case 'tight':
          return css`
            ${getTextStyle({ size: 'xxxs' })}
            min-width: 150px;
          `;
        case 'normal':
          return css``;
      }
    }}
  }
  p.subscription-table-header-text {
    font-weight: 600;
    ${props => {
      switch (props.layout) {
        case 'tight':
          return css`
            ${getTextStyle({ size: 'medium' })}
          `;
        case 'normal':
          return css`
            ${getTextStyle({ size: 'large' })}
          `;
      }
    }}
  }
  a.subscription-table-small-link {
    display: inline-block;
    margin: 1em 0;
    text-decoration: underline;
    cursor: pointer;
    ${props => {
      switch (props.layout) {
        case 'tight':
          return css`
            ${getTextStyle({ size: 'xxxs' })}
          `;
        case 'normal':
          return css`
            ${getTextStyle({ size: 'xxs' })}
          `;
      }
    }}
  }
  p.subscription-table-small-text {
    margin: 1em 0;
    ${props => {
      switch (props.layout) {
        case 'tight':
          return css`
            ${getTextStyle({ size: 'xxxs' })}
          `;
        case 'normal':
          return css`
            ${getTextStyle({ size: 'xxs' })}
          `;
      }
    }}
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  border-color: ${BouColor.TOOL_BORDER_LIGHT_GRAY};
  th {
    font-weight: unset;
  }
  button {
    font-weight: 600;
  }
`;
